import { ChevronLeftIcon, ChevronRightIcon, DoubleArrowLeftIcon, DoubleArrowRightIcon } from '@radix-ui/react-icons';
import { Button } from '@registry/new-york/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@registry/new-york/ui/select';
import { Table } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import styles from './DataTablePagination.module.scss';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  pagesSelect?: number[];
  hideRowsPerPage?: boolean;
}

export const DataTablePagination = <TData,>({
  table,
  pagesSelect,
  hideRowsPerPage = false,
}: DataTablePaginationProps<TData>) => {
  const [pageInputValue, setPageInputValue] = useState<string>((table.getState().pagination.pageIndex + 1).toString());
  const currentPage = table.getState().pagination.pageIndex + 1;

  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  const applyPageInput = (raw: string) => {
    const rawValue = raw.trim();
    const maxPage = Math.max(table.getPageCount(), 1);

    if (!rawValue) {
      // Empty input: revert to the latest valid page (current table page)
      setPageInputValue(currentPage.toString());
      return;
    }

    const value = Number(rawValue);

    if (!Number.isFinite(value) || value < 1 || value > maxPage) {
      // Invalid input: revert to the latest valid page (current table page)
      setPageInputValue(currentPage.toString());
      return;
    }

    setPageInputValue(value.toString());
    table.setPageIndex(value - 1);
  };

  const handlePageInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    applyPageInput(e.target.value);
  };

  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Disallow decimal separators
    if ((e.key === '.' || e.key === ',') && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    applyPageInput(e.currentTarget.value);
  };

  return (
    <div className={styles.wrapper}>
      <div />
      <div className="flex items-center space-x-6 lg:space-x-8">
        {!hideRowsPerPage && (
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={value => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={table.getState().pagination.pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {(pagesSelect || [10, 20, 30, 40]).map(pageSize => (
                  <SelectItem
                    key={pageSize}
                    value={`${pageSize}`}
                  >
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-center justify-center text-sm font-medium">
          Page
          <input
            className={styles.pageInput}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pageInputValue}
            onChange={e => setPageInputValue(e.target.value.replace(/[^\d]/g, ''))}
            onBlur={handlePageInputBlur}
            onKeyDown={handlePageInputKeyDown}
          />
          of {table.getPageCount()}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            type="button"
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <DoubleArrowLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <DoubleArrowRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
